// Libraries.
import { combineReducers, createStore, applyMiddleware, compose as composeWithoutDevTools } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import createSagaMiddleware from 'redux-saga'
import { cloneDeep, map, merge } from 'lodash/fp'
import { fork } from 'redux-saga/effects'
import compose from '@stamp/compose'
import Bottle from 'bottlejs'

// Helpers.
import { autoRestart, mergeValues } from './common/utils'
import { getVersion } from './common/version'

// Plugins.
import { logManager } from './logs'

// Parse and/or Validate
import { validation as v8n, parse } from './common/validation'
const log = logManager.getLogger('FACTORY')

const factoryDefaults = {
  enableReduxDevTools: false,
  reduxDevToolsName: 'WebRTC SDK',
  allowProxy: false
}

// config validation
const v8nValidation = v8n.schema({
  enableReduxDevTools: v8n.boolean(),
  reduxDevToolsName: v8n.string(),
  allowProxy: v8n.boolean()
})
const parseOptions = parse('common', v8nValidation)

/**
 * Creates an instance of the SDK with the specified plugins and configuration.
 *
 * @param {Function[]} pluginFactories - The list of factories to create plugins for this instance of the SDK.
 * @param {Object} [sdkOptions] - Factory and plugin options
 * @param {Object} [sdkOptions.common] - Factory specific options
 * @param {boolean} [sdkOptions.common.enableReduxDevTools] - A flag to indicate whether or not to include Redux Dev Tools
 * @param {string}  [sdkOptions.common.reduxDevToolsName="WebRTC SDK"] - The name of the store that will show up in the
 *                                          Redux Dev tools. This helps differentiate the SDK from other redux instances.
 * @param {boolean} [sdkOptions.allowProxy] Whether the factory should allow the Proxy Plugin to be included.
 */
export function factory (pluginFactories, sdkOptions = {}) {
  // Log the SDK's version (templated by webpack) on initialization.
  const version = getVersion()
  log.info(`SDK version: ${version}`)

  // Clone the options so we don't mutate the application's object.
  const clonedOptions = cloneDeep(sdkOptions)
  // Separate factory and plugin options.
  let { common: options, ...pluginOptions } = clonedOptions

  options = mergeValues(factoryDefaults, options)
  parseOptions(options)

  // Create a bottle which will act as a container for plugin components.
  const bottle = new Bottle()

  var sagas = []
  var store
  var middlewares = []
  const apis = []
  var reducers = {}
  var initSagas = []
  var taskDescriptor
  const shared = {}
  var context = {
    container: bottle.container,
    capabilities: [],
    api: {},
    primitives: {},
    getState () {
      if (!store) {
        throw Error('Store is not available during factory creation')
      }
      if (destroyedSDK) {
        throw Error('State is not available if the SDK has been destroyed')
      }
      return store.getState()
    },
    dispatch (...args) {
      if (!store) {
        throw Error('Store is not available during factory creation')
      }
      return store.dispatch(...args)
    },
    subscribe (fn) {
      if (!store) {
        throw Error('Store is not available during factory creation')
      }
      return store.subscribe(fn)
    }
  }

  // Add the context to the container so plugins can access it.
  bottle.value('context', context)

  // Special case middleware for logging.
  var loggerMiddleware

  var destroyedSDK = false
  var destroyedSDKMiddleware = store => next => action => {
    if (destroyedSDK) {
      return null
    } else {
      return next(action)
    }
  }

  // Instantiate the plugins.
  let plugins = map(function (plugin) {
    // Make sure the plugin factory is actually a function.
    if (!plugin || !plugin.fn || typeof plugin.fn !== 'function') {
      return false
    }

    // Provide the bottle to plugin factory functions so they can add their
    //    components.
    return plugin.fn(pluginOptions[plugin.name], bottle)
  }, pluginFactories)

  // Remove undefined plugins. Those are plugins that failed to load for some reason.
  plugins = plugins.filter(plugin => Boolean(plugin))

  // Run all the plugins to build the context.
  // Set up each plugin component individually.
  plugins.forEach(function (plugin) {
    // Ignore the Proxy Plugin if not configured to allow it.
    if (plugin.name === 'proxy' && !options.allowProxy) {
      plugin = {}
    }

    if (plugin.capabilities) {
      context.capabilities = context.capabilities.concat(plugin.capabilities)
    }
    if (plugin.reducer) {
      reducers[plugin.name] = plugin.reducer
    }
    if (plugin.middleware) {
      if (plugin.name === 'logs') {
        loggerMiddleware = plugin.middleware
      } else {
        middlewares.push(() => plugin.middleware(context)) // pass context to middleware instead of store
      }
    }
    if (plugin.api) {
      apis.push(plugin.api)
    }
    if (plugin.init) {
      initSagas.push(plugin.init)
    }
    if (plugin.sagas) {
      sagas = sagas.concat(plugin.sagas)
    }
    // TODO: Revisit this. Looks a little extreme for a couple stamps...
    if (plugin.mixins) {
      // Define a base stamp that all stamps will be composed with.
      //      This gives stamps access to the factory's context.
      const baseStamp = {
        propertyDescriptors: {
          context: {
            get () {
              return context
            }
          }
        }
      }
      for (const objName in plugin.mixins) {
        if (plugin.mixins.hasOwnProperty(objName)) {
          // Ensure that every stamp starts with the base stamp.
          if (!context.primitives.hasOwnProperty(objName)) {
            context.primitives[objName] = baseStamp
          }
          context.primitives[objName] = compose(context.primitives[objName], plugin.mixins[objName])
        }
      }
    }

    /**
     * Plugins are allowed to share data between them by providing it to the
     *    factory. All shared data will be collected as a single object, and
     *    that object will be provided to plugins during their `init` saga.
     * It is intended that a plugin can "override" another plugin's shared data.
     * The order that plugins are given to the factory matters because of this.
     * `plugin.shared` is expected to be an object.
     */
    if (plugin.shared) {
      for (const key in plugin.shared) {
        if (shared[key]) {
          log.debug(`Replacing shared ${key} with ${key} from ${plugin.name}.`)
          shared[key] = plugin.shared[key]
        } else {
          shared[key] = plugin.shared[key]
        }
      }
    }
  })

  if (loggerMiddleware) {
    // The redux logger middleware should be the last middleware.
    middlewares.push(() => loggerMiddleware(context))
  }

  // Compose the root saga
  function * rootSaga () {
    // Give all plugins an opportunity to initialize themselves.
    // Note: Sagas have not been forked yet, so init cannot use them
    //      by dispatching actions. See PR #698.
    // Note: If anything asynchronous is done in an init, it MUST be
    //      non-blocking (ie. use fork, not call). See PR #699.
    for (const init of initSagas) {
      // Note: Yield directly to init. If redux-saga's `call()` is used, then the
      //      factory returns before the instance is done all inits. See PR #699.
      // Provide the factory's shared data (from plugins) to the `init` saga.
      yield * init(shared)
    }
    // Run all of the sagas provided by implementation plugins.
    for (const saga of sagas) {
      yield fork(autoRestart(saga))
    }
  }

  // Alias our composeMiddleware to conditionally include devTools as per the provided configuration flag
  const composeMiddleware = options.enableReduxDevTools
    ? composeWithDevTools({ name: options.reduxDevToolsName })
    : composeWithoutDevTools

  // don't include saga stuff if there are no sagas.
  if (initSagas.length + sagas.length > 0) {
    var sagaMiddleware = createSagaMiddleware()

    // Create the store with the plugins (incl. sagas) and with the configuration as the initial state.
    store = createStore(
      combineReducers(reducers),
      composeMiddleware(applyMiddleware(destroyedSDKMiddleware, sagaMiddleware, ...middlewares))
    )

    taskDescriptor = sagaMiddleware.run(rootSaga)
  } else {
    // Create the store with the plugins (excl. sagas) and with the configuration as the initial state.
    store = createStore(
      combineReducers(reducers),
      composeMiddleware(applyMiddleware(destroyedSDKMiddleware, ...middlewares))
    )
  }

  // Use the plugins' API factories to build the public API.
  //    Do this after the store has been created.
  // Plugins can either have provided their API factory by:
  //    - returning it directly from its factory function,
  //    - registering it to the bottle.
  apis.forEach(apiFactory => {
    context.api = merge(context.api, apiFactory(context))
  })
  bottle.list().forEach(name => {
    // Find API components on the bottle and add them to the SDK API.
    if (name.toLowerCase().endsWith('api')) {
      const api = bottle.container[name]
      context.api = merge(context.api, api)
    }
  })

  // Resolve all deferred functions. Provide them the container so they have the
  //    same parameters as registered components.
  bottle.resolve(bottle.container)

  // setup the API
  const publicAPI = {
    ...context.api,
    state: {
      get: function () {
        return store.getState()
      },
      subscribe: function (...args) {
        return store.subscribe(...args)
      }
    },
    /**
     * Return all the capabilities currently available to the plugin system.
     *
     * @memberof api
     * @method getCapabilities
     */
    getCapabilities () {
      return context.capabilities
    },
    /**
     * Returns the current version of the API.
     *
     * @public
     * @memberof api
     * @method getVersion
     */
    getVersion () {
      return version
    },
    // Disabling eslint for the next comment as we want to be able to use a disallowed word
    // eslint-disable-next-line no-warning-comments
    /**
     * Destroys the SDK, and removes its state, rendering the SDK unusable.
     * Useful when a user logs out and their call data needs to be destroyed.
     * The SDK must be recreated to be usable again.
     * The destroy command is async, and will happen on the next tick
     *   so as not to interfere with any ongoing events.
     *
     * @public
     * @memberof api
     * @method destroy
     * @example
     * // Instantiate the SDK.
     * import { create } from 'kandy'
     * const config = {
     *     authentication: { ... },
     *     logs: { ... },
     *     ...
     * }
     * let client = create(config);
     * client.on( ... )
     * // Use the SDK
     * ...
     * // Destroy the SDK, then recreate on the next step
     * client.destroy()
     * client = create(config)
     * client.on( ... )
     */
    destroy () {
      // TODO: Is it possible for the store to auto-unsubscribe any listeners
      //    (from client.state.subscribe API)? If not, may be easier to simply
      //    protect from issues.

      const _destroy = () => {
        if (destroyedSDK) return

        // Cancel all the sagas
        if (taskDescriptor) taskDescriptor.cancel()

        // Clear the state
        function destroyStateReducer (state, action) {
          return {}
        }
        store.replaceReducer(destroyStateReducer)

        // Neuter all actions
        destroyedSDK = true
      }

      // setTimeout is necessary so that the destroy
      //   happens at the start of the next tick for
      //   when destroy is called inside an event handler
      const immediately = 0
      setTimeout(_destroy, immediately)
    }
  }

  // Return the public API.
  return publicAPI
}
